import { type BooleanElement } from '..';
import { type GenericPolyElement } from '../../poly';

type MaskCacheEntry = {
  height: number;
  key: string;
  mask: HTMLCanvasElement;
  width: number;
};

const maskCache = new Map<string, MaskCacheEntry>();

function buildMaskKey(
  context: CanvasRenderingContext2D,
  element: BooleanElement,
  children: GenericPolyElement[],
) {
  const transform = context.getTransform();

  return [
    element.x,
    element.y,
    context.canvas.width,
    context.canvas.height,
    transform.a,
    transform.b,
    transform.c,
    transform.d,
    transform.e,
    transform.f,
    ...children.map(
      (element) =>
        `${element.id}:${JSON.stringify(element.version)}:${element.booleanMode}:${element.strokeWidth}`,
    ),
  ].join('|');
}

const pathCache = new Map<
  string,
  {
    path: Path2D;
    shapeData: string;
  }
>();

function paintElement(
  context: CanvasRenderingContext2D,
  element: GenericPolyElement,
) {
  const cached = pathCache.get(element.id);

  if (cached?.shapeData === element.shape.data) {
    context.fill(cached.path);
    return;
  }

  const path = new Path2D(element.shape.data);
  pathCache.set(element.id, {
    path,
    shapeData: element.shape.data,
  });
  context.fill(path);
}

export function getBooleanMask(
  context: CanvasRenderingContext2D,
  element: BooleanElement,
  children: GenericPolyElement[],
): HTMLCanvasElement {
  const { width, height } = context.canvas;
  const transform = context.getTransform();

  const key = buildMaskKey(context, element, children);
  const cached = maskCache.get(element.id);
  if (
    cached &&
    cached.key === key &&
    cached.width === width &&
    cached.height === height
  ) {
    return cached.mask;
  }

  const mask = cached?.mask ?? document.createElement('canvas');
  mask.width = width;
  mask.height = height;

  const maskContext = mask.getContext('2d')!;
  maskContext.setTransform(transform);
  maskContext.translate(-element.x, -element.y);
  maskContext.fillStyle = '#000';

  children.forEach((element, index) => {
    if (index === 0 && element.booleanMode === 'subtract') return;

    maskContext.globalCompositeOperation =
      index === 0 || element.booleanMode === 'union'
        ? 'source-over'
        : element.booleanMode === 'subtract'
          ? 'destination-out'
          : 'xor';

    paintElement(maskContext, element);
  });

  maskContext.globalCompositeOperation = 'source-over';
  maskCache.set(element.id, {
    key,
    mask,
    width,
    height,
  });

  return mask;
}

export const clearBooleanCaches = () => {
  maskCache.clear();
  pathCache.clear();
};

export const clearBooleanCacheForElement = (elementId: string) => {
  maskCache.delete(elementId);
  pathCache.delete(elementId);
};
